import React, { useMemo } from 'react';
import { Button } from 'antd';
import moment from 'moment';
import { IBestDataset } from '../../../../../../types/analytics';
import { IEnrichedMetadata } from '../../../../../../types/metadata';
import { IAdvancedAnalyticsApiPayload } from '../../../../../../types/chart';
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput';
import { MarkerItem } from '../../../../../../types/home';
import { usePersistenceParams } from '../../../hooks/usePersistenceParams';
import { AnalyticsSection } from '../../Layout/AnalyticsSection';
import PersistenceInputForm from './PersistenceInputForm';
import PersistenceChart from './PersistenceChart';
import PersistenceAdditionalInput from './PersistenceAdditionalInput';

interface PersistenceProps {
  coordinates: number[];
  previewDatasetId: string;
  categoryDataset: IBestDataset[];
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  persistenceImage: string;
  persistenceLinks: string[];
  updating: boolean;
  error: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  onDatasetChange: (item: string) => void;
  gridCellSize: string;
  waterDepth: string;
  point?: MarkerItem;
  onChangeActiveKey: (item: any) => void;
  pointOption: { label: string; value: string }[];
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  isOfflineDataset: boolean;
}

export default function Persistence(props: PersistenceProps) {
  const {
    categoryDataset,
    persistenceImage,
    persistenceLinks,
    updating,
    error,
    pointName,
    point,
    pointOption,
    onChangeActiveKey,
    isOfflineDataset,
  } = props;

  const {
    variablesX,
    variablesY,
    variableX,
    variableY,
    dataset2,
    duration,
    sign,
    sign2,
    percentile,
    threshold,
    threshold2,
    conditionalDSSelected,
    IsChangeDatasetConditional,
    startDate,
    endDate,
    categories,
    onUpdatePersistence,
    doVariableChange,
    doVariableYChange,
    onChangeDuration,
    doSignChange,
    doSign2Change,
    doPercentileChange,
    doDataset2Change,
    onChangeThreshold,
    onChangeThreshold2,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  } = usePersistenceParams(props);

  // TODO: enable cache
  // const upsertPersistenceCache = useAnalyticsCacheStore(state => state.upsertPersistenceCache)

  // useEffect(() => {
  //   if (!props.coordinates || !props.previewDatasetId || !variableX?.Index) return;

  //   const key = `${props.previewDatasetId}_${variableX.Index}_${props.coordinates[1]},${props.coordinates[0]}`;
  //   upsertPersistenceCache(key, {
  //     variableXIndex: variableX.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     persistenceImage,
  //     persistenceLinks
  //   });
  // }, [upsertPersistenceCache, props.coordinates, props.previewDatasetId, variableX?.Index, startDate, endDate, persistenceImage, persistenceLinks]);

  const plotInputContent = useMemo(
    () => (
      <PersistenceInputForm
        pointOption={pointOption}
        categoryDataset={categoryDataset}
        dataset={props.previewDatasetId}
        onSelectDataset={onSelectDataset}
        categories={categories}
        variablesX={variablesX}
        variableX={variableX}
        onVariableChange={doVariableChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={doStartDateChange}
        onEndDateChange={doEndDateChange}
        disabledStartDate={disabledStartDate}
        disabledEndDate={disabledEndDate}
        isForAllAnalytics={props.isForAllAnalytics}
        onCheckboxChange={handleCheckboxChange}
        isLoading={updating}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [
      pointOption,
      point,
      onChangeActiveKey,
      categoryDataset,
      props.previewDatasetId,
      onSelectDataset,
      categories,
      variablesX,
      variableX,
      doVariableChange,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      props.isForAllAnalytics,
      handleCheckboxChange,
      updating,
      error,
    ]
  );

  const updateContent = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '36px',
        }}
      >
        <Button
          type='primary'
          id='Analytics-Persistence-Update_Button'
          disabled={updating || isOfflineDataset}
          loading={updating}
          onClick={() => onUpdatePersistence()}
          style={{
            width: '90px',
            height: '36px',
          }}
        >
          Update
        </Button>
      </div>
    ),
    [updating, onUpdatePersistence]
  );

  const chartContent = useMemo(
    () => (
      <PersistenceChart
        persistenceImage={persistenceImage}
        persistenceLinks={persistenceLinks}
        pointName={pointName}
        isLoading={updating}
        error={error}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [persistenceImage, persistenceLinks, pointName, updating, error]
  );

  const additionalContent = useMemo(
    () => (
      <PersistenceAdditionalInput
        sign={sign}
        percentile={percentile}
        duration={duration}
        threshold={threshold}
        dataset2={dataset2}
        variablesY={variablesY}
        variableY={variableY}
        sign2={sign2}
        threshold2={threshold2}
        categories={categories}
        categoryDataset={categoryDataset}
        conditionalDSSelected={conditionalDSSelected}
        IsChangeDatasetConditional={IsChangeDatasetConditional}
        onSignChange={doSignChange}
        onPercentileChange={doPercentileChange}
        onDurationChange={onChangeDuration}
        onThresholdChange={onChangeThreshold}
        onDataset2Change={doDataset2Change}
        onVariableYChange={doVariableYChange}
        onSign2Change={doSign2Change}
        onThreshold2Change={onChangeThreshold2}
        isLoading={updating}
      />
    ),
    [
      sign,
      percentile,
      duration,
      threshold,
      dataset2,
      variablesY,
      variableY,
      sign2,
      threshold2,
      categories,
      categoryDataset,
      conditionalDSSelected,
      IsChangeDatasetConditional,
      doSignChange,
      doPercentileChange,
      onChangeDuration,
      onChangeThreshold,
      doDataset2Change,
      doVariableYChange,
      doSign2Change,
      onChangeThreshold2,
      updating,
      error,
    ]
  );

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  );
}
